// =============================================================================
// NewRemittancePage — driver picks COD payments and submits a bundle
// =============================================================================
// Lists every Payment(isDeliveryCod=true, voidedAt=null) NOT already attached
// to a remittance (the API filters those). Driver ticks the ones in their
// envelope, sees a live total, submits.

import { type MouseEvent as ReactMouseEvent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Money } from '../../../components/Money';
import { PageScaffold } from '../../../components/PageScaffold';
import { cn } from '../../../lib/cn';

interface CodPayment {
  id: string;
  amount: string;
  reference: string | null;
  recordedAt: string;
  order: {
    id: string;
    number: string;
    customer: { name: string; companyName: string | null; type: 'individual' | 'company' };
  };
}

export function NewRemittancePage(): JSX.Element {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // The /payments endpoint isn't a generic list — we filter on the client by
  // walking orders with delivery-cod payments. For now, surface only orders
  // marked with COD payments via a dedicated /payments?cod=open query.
  // (We don't have that endpoint yet — for the demo, fetch via /orders and
  // walk payments client-side.)
  const orders = useQuery({
    queryKey: ['orders-with-cod'],
    queryFn: () =>
      apiGet<
        Array<{
          id: string;
          number: string;
          customer: { name: string; companyName: string | null; type: 'individual' | 'company' };
          payments: Array<{
            id: string;
            amount: string;
            reference: string | null;
            recordedAt: string;
            isDeliveryCod: boolean;
            voidedAt: string | null;
            remittanceLine: { id: string } | null;
          }>;
        }>
      >('/orders'),
  });

  const available: CodPayment[] = useMemo(() => {
    const out: CodPayment[] = [];
    for (const o of orders.data ?? []) {
      for (const p of o.payments ?? []) {
        if (p.isDeliveryCod && !p.voidedAt && !p.remittanceLine) {
          out.push({
            id: p.id,
            amount: p.amount,
            reference: p.reference,
            recordedAt: p.recordedAt,
            order: { id: o.id, number: o.number, customer: o.customer },
          });
        }
      }
    }
    return out.sort((a, b) => +new Date(b.recordedAt) - +new Date(a.recordedAt));
  }, [orders.data]);

  const total = useMemo(
    () => available.filter((p) => selected.has(p.id)).reduce((sum, p) => sum + Number(p.amount), 0),
    [available, selected],
  );

  const submit = useMutation({
    mutationFn: () =>
      apiPost<{ id: string }>('/driver-remittances', {
        paymentIds: [...selected],
        notes: notes || undefined,
      }),
    onSuccess: (r) => navigate(`/remittances/${r.id}`),
    onError: (e) => setErr(getApiError(e).message),
  });

  const toggle = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const FOOTER_ACTIONS = (
    <Button
      disabled={selected.size === 0 || submit.isPending}
      onClick={() => {
        setErr(null);
        submit.mutate();
      }}
    >
      {submit.isPending ? 'Submitting…' : 'Submit to HQ'}
    </Button>
  );

  return (
    <PageScaffold
      title="Submit cash to HQ"
      subtitle="Tick every delivery-COD payment whose cash is in your envelope. Finance will count and acknowledge."
      back={{ to: '/remittances', label: 'Remittances' }}
      width="form"
      footer={FOOTER_ACTIONS}
    >
      <Card title="Delivery-COD payments awaiting remittance" bodyClassName="p-0">
        {orders.isLoading ? (
          <div className="text-center text-ink-3 py-12 text-sm">Loading…</div>
        ) : available.length === 0 ? (
          <div className="text-center text-ink-3 py-12 text-sm italic">
            No outstanding delivery-COD payments. Record a payment at delivery with “COD” checked
            first.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-4 py-2 w-12" />
                <th className="px-6 py-2 font-medium">Order</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium">Recorded</th>
                <th className="px-6 py-2 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {available.map((p) => {
                const display =
                  p.order.customer.type === 'company'
                    ? (p.order.customer.companyName ?? p.order.customer.name)
                    : p.order.customer.name;
                const checked = selected.has(p.id);
                return (
                  <tr
                    key={p.id}
                    className={cn(
                      'border-b border-rule-2 cursor-pointer',
                      checked && 'bg-accent-soft',
                    )}
                    onClick={() => toggle(p.id)}
                  >
                    <td className="px-4 py-3 text-center">
                      <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} />
                    </td>
                    <td className="px-6 py-3">
                      <Link
                        to={`/orders/${p.order.id}`}
                        className="kc-mono text-xs text-ink hover:text-accent"
                        onClick={(e: ReactMouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
                      >
                        {p.order.number}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-ink">{display}</td>
                    <td className="px-6 py-3 text-xs text-ink-3">
                      {new Date(p.recordedAt).toLocaleString('en-MU')}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Money value={p.amount} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card title="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Friday Belle Mare round, 6 stops"
              className="kc-input min-h-[80px] w-full text-sm"
            />
          </Card>
        </div>
        <Card title="Bag total">
          <div className="text-2xl kc-serif text-accent">
            <Money value={total} />
          </div>
          <div className="text-xs text-ink-3 mt-1">
            {selected.size} payment{selected.size === 1 ? '' : 's'} selected
          </div>
          {err && <div className="text-xs text-negative mt-3">{err}</div>}
        </Card>
      </div>
    </PageScaffold>
  );
}
